import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const ScrollContext = createContext();

export const useScroll = () => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error('useScroll must be used within ScrollProvider');
  }
  return context;
};

export const ScrollProvider = ({ children }) => {
  const [navbarOffset, setNavbarOffset] = useState(0);
  const [isAtTop, setIsAtTop] = useState(true);
  const lastScrollY = useRef(0);

  const getNavbarHeight = useCallback(() => {
    return typeof window !== 'undefined' ? (window.innerWidth >= 640 ? 80 : 64) : 64;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const navbarHeight = getNavbarHeight();
      const scrollDelta = currentScrollY - lastScrollY.current;
      const scrollingDown = scrollDelta > 0;
      const scrollingUp = scrollDelta < 0;
      
      setIsAtTop(currentScrollY < 50);
      
      // Get the category tabs element
      const categoryTabs = document.querySelector('[data-category-tabs]');
      if (!categoryTabs) {
        lastScrollY.current = currentScrollY;
        return;
      }
      
      const categoryRect = categoryTabs.getBoundingClientRect();
      
      // Category tabs are sticky at navbarHeight from top
      // When they touch the navbar (categoryRect.top <= navbarHeight), start pushing
      
      if (scrollingDown && currentScrollY > 100) {
        // Check if categories have reached (touched) the navbar
        // Categories touch navbar when their top equals navbar bottom
        if (categoryRect.top <= navbarHeight + 1) {
          // They're touching! Push navbar up
          const newOffset = Math.max(-navbarHeight, navbarOffset - Math.abs(scrollDelta));
          setNavbarOffset(newOffset);
        }
      } else if (scrollingUp) {
        // When scrolling up, bring navbar back gradually
        if (navbarOffset < 0) {
          const newOffset = Math.min(0, navbarOffset + Math.abs(scrollDelta));
          setNavbarOffset(newOffset);
        }
      }
      
      // Reset when at very top
      if (currentScrollY < 50) {
        setNavbarOffset(0);
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navbarOffset, getNavbarHeight]);

  // Category stays at navbar height, but moves up with navbar when pushed
  const navbarHeight = getNavbarHeight();
  const categoryOffset = Math.max(0, navbarHeight + navbarOffset);

  return (
    <ScrollContext.Provider value={{ navbarOffset, categoryOffset, isAtTop }}>
      {children}
    </ScrollContext.Provider>
  );
};
